import React, { useEffect, useRef } from 'react';
import Input from '../../../components/inputs/Input';
import Checkbox from '../../../components/inputs/Checkbox';
import { EmployeePostRequest } from '../../../types/employee/EmployeePostRequest';
import { focusNextInput } from '../../../utils/focusNextInput';
import SubmitAndCancelButton from '../../../components/button/SubmitAndCancelButton';

interface AddEmployeeRowProps {
  item: EmployeePostRequest;
  onChange: (key: keyof EmployeePostRequest, value: string | boolean) => void;
  onAddSubmitButtonClick: () => void;
  onSubmitCancelButtonClick: () => void;
}

// 英数字のみで8文字のパスワードを生成
const generateAlnum = (length = 8) => {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const buf = new Uint32Array(length);
    crypto.getRandomValues(buf);
    let out = '';
    for (let i = 0; i < length; i++) out += chars[buf[i] % chars.length];
    return out;
  }
  // フォールバック
  let out = '';
  for (let i = 0; i < length; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
};

const AddEmployeeRow: React.FC<AddEmployeeRowProps> = ({
  item,
  onChange,
  onAddSubmitButtonClick,
  onSubmitCancelButtonClick,
}) => {
  const inputsRef = useRef<(HTMLInputElement | HTMLButtonElement)[]>([]);
  const initRef = useRef(false);

  // パスワードを生成
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    if (!item.password || String(item.password).trim().length === 0) {
      const pw = generateAlnum();
      onChange('password', pw);
    }
  }, [item.password, onChange]);

  // 最初の input に自動フォーカス
  useEffect(() => {
    if (inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, []);

  return (
    <>
      <td>
        <Input
          value={item.employeeCode}
          onChange={(e) => onChange('employeeCode', e.target.value)}
          ref={(el) => {
            if (el && !inputsRef.current.includes(el)) {
              inputsRef.current.push(el);
            }
          }}
          onKeyDown={(e) => focusNextInput(e, inputsRef, 0)}
        />
      </td>
      <td>
        <Input
          value={item.name}
          onChange={(e) => onChange('name', e.target.value)}
          ref={(el) => {
            if (el && !inputsRef.current.includes(el)) {
              inputsRef.current.push(el);
            }
          }}
          onKeyDown={(e) => focusNextInput(e, inputsRef, 1)}
        />
      </td>
      <td>
        <Input
          value={item.password}
          onChange={(e) => onChange('password', e.target.value)}
          ref={(el) => {
            if (el && !inputsRef.current.includes(el)) {
              inputsRef.current.push(el);
            }
          }}
          onKeyDown={(e) => focusNextInput(e, inputsRef, 2)}
        />
      </td>
      <td>
        <Checkbox
          checked={item.isDairen}
          onChange={(checked) => onChange('isDairen', checked)}
          ref={(el) => {
            if (el && !inputsRef.current.includes(el)) {
              inputsRef.current.push(el);
            }
          }}
          onKeyDown={(e) => focusNextInput(e, inputsRef, 3)}
        />
      </td>
      <td>
        <Checkbox
          checked={item.isAdmin}
          onChange={(checked) => onChange('isAdmin', checked)}
          ref={(el) => {
            if (el && !inputsRef.current.includes(el)) {
              inputsRef.current.push(el);
            }
          }}
          onKeyDown={(e) => focusNextInput(e, inputsRef, 4)}
        />
      </td>
      <td className="text-center">
        <SubmitAndCancelButton
          onSubmit={() => onAddSubmitButtonClick()}
          onCancel={() => onSubmitCancelButtonClick()}
          submitButtonRef={(el) => {
            if (el && !inputsRef.current.includes(el)) {
              inputsRef.current.push(el);
            }
          }}
          cancelButtonRef={(el) => {
            if (el && !inputsRef.current.includes(el)) {
              inputsRef.current.push(el);
            }
          }}
          onSubmitKeyDown={(e) => focusNextInput(e, inputsRef, 5)}
          onCancelKeyDown={(e) => focusNextInput(e, inputsRef, 6)}
        />
      </td>
    </>
  );
};

export default AddEmployeeRow;
